import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { RecipeEditorPanel } from "../../../production/straightener-common/components/RecipeEditorPanel";
import { useStraightenerRecipeEditor } from "../../../production/straightener-common/hooks/useStraightenerRecipeEditor";
import { buildProductNodes } from "../../../production/straightener-common/productNodes";
import {
	useCurrentCrew,
	useStraightenerProducts,
	useStraightenerRecipes,
} from "../../../production/straightener-recipes/hooks/useStraightenerRecipes";
import type {
	RecipeProposalRequest,
	RecipeProposalStat,
} from "../models/StraightenerInformationRetrieval";
import { getStraightenerRecipeProposal } from "../repositories/StraightenerInformationRetrievalRepository";

/** A value whose runs spread wider than this did not hold steady in the period. */
const VARIED_SPREAD = 0.01;

const format = (value: number) => Number(value.toFixed(3)).toString();

interface Props {
	open: boolean;
	request: RecipeProposalRequest | null;
	onClose: () => void;
	onSaved: () => void;
}

/**
 * Build Recipe from the straightener runs: the proposal opens in the same recipe
 * editor the Recipes page uses, and nothing is written until the user saves. A
 * save is always a new version, through the existing recipe save procedure.
 */
export const BuildStraightenerRecipeModal = ({
	open,
	request,
	onClose,
	onSaved,
}: Props) => {
	const editor = useStraightenerRecipeEditor({ onSavedNew: onSaved });
	const productsQ = useStraightenerProducts();
	const recipesQ = useStraightenerRecipes();
	const crewQ = useCurrentCrew();

	const proposalQ = useQuery({
		queryKey: ["straightenerRecipeProposal", request],
		queryFn: () =>
			getStraightenerRecipeProposal(request as RecipeProposalRequest),
		enabled: open && request != null,
		staleTime: 0,
		cacheTime: 0,
	});

	// Seed the editor once per proposal; the user's edits must not be overwritten.
	const { setNewRecipeValues } = editor;
	useEffect(() => {
		if (proposalQ.data) setNewRecipeValues(proposalQ.data.values);
	}, [proposalQ.data, setNewRecipeValues]);

	const allNodes = useMemo(
		() => buildProductNodes(productsQ.data ?? [], recipesQ.data ?? []),
		[productsQ.data, recipesQ.data],
	);
	const product =
		productsQ.data?.find((p) => p.Id === request?.productSizeId) ?? null;
	const existingNames = useMemo(
		() =>
			(
				allNodes.find((n) => n.productSizeId === request?.productSizeId)
					?.versions ?? []
			)
				.map((v) => (v.Name ?? "").trim().toLowerCase())
				.filter(Boolean),
		[allNodes, request?.productSizeId],
	);

	const varied = useMemo<RecipeProposalStat[]>(
		() =>
			(proposalQ.data?.stats ?? []).filter(
				(s) => s.MaxValue - s.MinValue > VARIED_SPREAD,
			),
		[proposalQ.data],
	);

	const summary = proposalQ.data?.summary;
	const proposedName = request
		? `From runs ${request.start.slice(5)} to ${request.end.slice(5)}`
		: "";

	return (
		<>
			<MesfModal
				id="modal-build-straightener-recipe"
				title="Build recipe from straightener runs"
				open={open}
				handleClose={onClose}
				maxWidth="xl"
			>
				<MesfModal.Content dividers style={{ padding: "12px 24px" }}>
					{proposalQ.isInitialLoading || productsQ.isInitialLoading ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
							<CircularProgress />
						</Box>
					) : proposalQ.error ? (
						<Typography color="error" sx={{ py: 4 }}>
							{(proposalQ.error as Error).message}
						</Typography>
					) : (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
							<Typography variant="body2">
								Proposed from <strong>{summary?.Runs ?? 0} runs</strong> of the
								ticked groups: each setpoint takes the value most runs used.
								Review it, then save it as a new version. It only becomes the
								current recipe if you choose so.
							</Typography>

							{varied.length > 0 ? (
								<Box
									sx={{
										border: "1px solid #f0c36d",
										bgcolor: "#fff8e6",
										borderRadius: 1,
										px: 1.5,
										py: 1,
									}}
								>
									<Typography variant="body2" sx={{ fontWeight: 600 }}>
										Changed during these runs ({varied.length}), check them:
									</Typography>
									<Typography variant="caption" component="div">
										{varied
											.map(
												(s) =>
													`${s.ParameterName}${s.RollNumber ? ` R${s.RollNumber}` : ""}: ${format(s.ModeValue)} in ${Math.round(s.ModeShare)}% of runs (min ${format(s.MinValue)}, max ${format(s.MaxValue)})`,
											)
											.join(" · ")}
									</Typography>
								</Box>
							) : null}

							<Box sx={{ height: "calc(85vh - 220px)", minHeight: 420 }}>
								<RecipeEditorPanel
									product={product}
									recipeId={null}
									version={null}
									recipeName={proposedName}
									selectedVersion={null}
									allNodes={allNodes}
									existingNames={existingNames}
									// The crew on shift, or else the crew of the first ticked group.
									defaultCrew={
										crewQ.data ??
										request?.groups.find((g) => g.shift)?.shift ??
										""
									}
									editor={editor}
									showPrefill={false}
									newRecipeTitle="Recipe from runs"
									onClose={onClose}
								/>
							</Box>
						</Box>
					)}
				</MesfModal.Content>
			</MesfModal>

			<ErrorModal error={editor.error} onHide={() => editor.setError("")} />
		</>
	);
};
